/**
 * Reusable mobile-first datetime picker for admin forms.
 *
 * Wraps an <input type="datetime-local"> that renders the phone's native
 * date+time keyboard on iOS/Android. Emits an ISO 8601 UTC string
 * (matches the backend's existing storage format so contest countdown
 * logic stays unchanged).
 *
 * Props:
 *   value         — current ISO string or ""
 *   onChange(iso) — called with new ISO string (or "" when cleared)
 *   testId
 *   label
 *   hint          — small helper text below the field
 */

const isoToLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  // datetime-local expects "YYYY-MM-DDTHH:mm" in LOCAL time — no seconds/TZ.
  const off = d.getTimezoneOffset();
  const local = new Date(d.getTime() - off * 60 * 1000);
  return local.toISOString().slice(0, 16);
};

const localInputToIso = (v) => {
  if (!v) return "";
  const d = new Date(v);   // interpreted as local by JS
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString();  // full UTC ISO for the backend
};

export const DateTimeField = ({
  value,
  onChange,
  testId = "datetime-field",
  label,
  hint,
  required = false,
}) => {
  return (
    <div>
      {label && (
        <label className="text-[11px] uppercase tracking-widest text-gray-400 font-black block mb-1">
          {label} {required && <span className="text-rose-400">*</span>}
        </label>
      )}
      <input
        type="datetime-local"
        value={isoToLocalInput(value)}
        onChange={(e) => onChange(localInputToIso(e.target.value))}
        className="w-full h-10 rounded-md bg-white/5 border border-white/10 text-white px-3 text-sm"
        data-testid={testId}
      />
      {hint && (
        <p className="text-[10px] text-gray-500 mt-1">{hint}</p>
      )}
      {value && (
        <p className="text-[10px] text-emerald-300 mt-1" data-testid={`${testId}-iso`}>
          Saved as: <span className="font-mono">{value}</span>
        </p>
      )}
    </div>
  );
};

export default DateTimeField;
